import { cva, type VariantProps } from 'class-variance-authority';
import {
  forwardRef,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  Info,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router';
import { cn } from '@/lib/cn';

/**
 * The primitive component set, transcribed from docs/design/DESIGN.md.
 *
 * Every variant here corresponds to a documented decision in that file — pill buttons,
 * 20px card radius, 12px inputs, diffused purple-tinted shadows. Keeping them in one
 * module makes the design system reviewable as a whole: a developer adding a screen
 * composes these rather than reaching for raw Tailwind and quietly inventing a fifth
 * shade of purple.
 */

// -----------------------------------------------------------------------------
// Layout
// -----------------------------------------------------------------------------

const containerPadding = {
  /** 16px on mobile, widening to the design's 24px gutter from `md`. */
  default: 'px-4 md:px-6',
  /** A flat 24px gutter, which is what the reference design's older blocks use. */
  gutter: 'px-6',
  /** 24px, widening to 32px on large screens — the reference design's newer blocks. */
  wide: 'px-6 lg:px-8',
} as const;

export interface ContainerProps extends ComponentPropsWithoutRef<'div'> {
  pad?: keyof typeof containerPadding;
}

/** 1240px max-width, centred, with the design's 24px gutters. */
export function Container({
  className,
  children,
  pad = 'default',
  ...props
}: ContainerProps): ReactNode {
  return (
    <div
      className={cn('mx-auto w-full max-w-[1240px]', containerPadding[pad], className)}
      {...props}
    >
      {children}
    </div>
  );
}

const sectionVariants = cva(
  /*
    The shared rhythm, same value as `sectionRhythm` above.

    This is the base, not the `default` size — `size: { default: '' }` overrides nothing,
    so every `<Section>` without an explicit size was silently carrying the old 120px.
    That was the single largest source of dead space on the site: a section setting only
    `pt-10` still ended on 120px of padding, and the page beneath it added its own.
  */
  'relative w-full py-12 md:py-16',
  {
    variants: {
      tone: {
        /** Pure white — the default depth layer. */
        surface: 'bg-surface',
        /** Lavender section wash, to separate blocks without drawing lines. */
        wash: 'bg-surface-wash',
        /** Deep purple, for high-contrast bands and the footer. */
        ink: 'bg-ink text-white',
        /** Subtle tint, used between two white sections. */
        tint: 'bg-surface-tint',
      },
      size: {
        default: '',
        /** Kept as an explicit opt-in, though it now matches the base. */
        compact: 'py-12 md:py-16',
        tight: 'py-8 md:py-12',
        /**
         * A page's tinted header band.
         *
         * The top clears the fixed nav pill, which ends at 94px — `pt-32` leaves 34px of
         * air under it. `md:pt-40` left 66px, which read as the page starting late, and
         * was 16px looser than the homepage hero for no reason a visitor could see.
         *
         * The bottom is deliberately much smaller than the standard rhythm: the band and
         * the first content section below it read as one unit, and two full paddings back
         * to back left a 200px hole between the page title and the first thing under it.
         */
        hero: 'pt-28 pb-8 md:pt-32 md:pb-10',
      },
    },
    defaultVariants: { tone: 'surface', size: 'default' },
  },
);

/**
 * The vertical padding every mid-page section uses.
 *
 * One constant rather than a literal per section. The page had drifted to five different
 * values — 96px, 120px, 64px and two asymmetric pairs — and because the gap a reader
 * actually sees is the sum of two of them, the boundaries ranged from 99px to 217px.
 * That reads as sections randomly floating apart rather than as hierarchy. 64px a side
 * puts every boundary at an even 128px, 96px on a phone.
 *
 * Exported from here, not from `sections.tsx`: that file exports only components, and a
 * stray constant there costs it fast refresh.
 */
export const sectionRhythm = 'py-12 md:py-16';

export interface SectionProps
  extends ComponentPropsWithoutRef<'section'>, VariantProps<typeof sectionVariants> {}

export function Section({ className, tone, size, children, ...props }: SectionProps): ReactNode {
  return (
    <section className={cn(sectionVariants({ tone, size }), className)} {...props}>
      {children}
    </section>
  );
}

/**
 * A decorative background glow.
 *
 * `aria-hidden` because it conveys nothing — a screen reader announcing "image" here
 * would be pure noise.
 */
export function Glow({ className }: { className?: string }): ReactNode {
  return <div aria-hidden="true" className={cn('hero-glow', className)} />;
}

// -----------------------------------------------------------------------------
// Typography
// -----------------------------------------------------------------------------

/** Small uppercase label. The only place the design permits uppercase. */
export function Eyebrow({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<'p'>): ReactNode {
  return (
    <p
      className={cn(
        'text-eyebrow font-bold uppercase tracking-[0.1em] text-primary-strong',
        className,
      )}
      {...props}
    >
      {children}
    </p>
  );
}

interface SectionHeadingProps {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  align?: 'left' | 'center';
  className?: string;
  /** Renders as h1 on the page's primary heading; h2 elsewhere. */
  as?: 'h1' | 'h2';
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  align = 'center',
  className,
  as: Tag = 'h2',
}: SectionHeadingProps): ReactNode {
  return (
    <div
      className={cn(
        'flex flex-col gap-4',
        align === 'center' ? 'items-center text-center' : 'items-start text-left',
        className,
      )}
    >
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <Tag
        className={cn(
          'font-semibold tracking-[-0.01em]',
          Tag === 'h1'
            ? 'text-[2.5rem] leading-[1.1] md:text-[3.75rem]'
            : 'text-[2rem] leading-[1.2] md:text-[2.5rem]',
        )}
      >
        {title}
      </Tag>
      {description ? (
        <p className={cn('max-w-2xl text-body-lg text-body', align === 'center' && 'mx-auto')}>
          {description}
        </p>
      ) : null}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Button
// -----------------------------------------------------------------------------

const buttonVariants = cva(
  // Fully pill-shaped, per the design: organic curves against a structured grid.
  'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-all ' +
    'disabled:pointer-events-none disabled:opacity-50 whitespace-nowrap',
  {
    variants: {
      /*
        Every variant is a gradient, and that is the point rather than a flourish.

        A filled button carries a two-stop gradient in its own hue; an unfilled one — the
        three that sit on a light ground and must stay quieter than `primary` — carries a
        gradient *wash* that only appears on hover, so the same language reads across the
        marketing pages, the sign-in card and the admin console without flattening the
        hierarchy between a page's main action and its escape hatch.

        Gradients are declared `to-r` throughout, so two buttons sitting side by side on a
        row light in the same direction. A `to-br` in the mix reads as one of them being
        lit from somewhere else.
      */
      variant: {
        /** Purple gradient, brightening on hover. The page's primary action. */
        primary:
          'bg-gradient-to-r from-primary-strong to-primary text-on-primary shadow-soft ' +
          'hover:brightness-110 hover:shadow-lifted active:brightness-95',
        /**
         * White with a 1px primary border; the gradient arrives as a wash on hover.
         *
         * Not `.text-gradient` on the label, which was the obvious idea and is wrong here:
         * that utility sets `background-clip: text`, and clipping applies to the element's
         * background-*colour* as much as its image — so the white fill would be clipped to
         * the glyphs too and the button would lose its ground entirely.
         */
        secondary:
          'bg-surface text-primary-strong border border-primary/40 shadow-soft ' +
          'hover:border-primary hover:shadow-lifted active:brightness-95 ' +
          'hover:bg-gradient-to-r hover:from-primary/12 hover:to-primary/5',
        /** Deep purple fill, for use on a light wash. */
        ink: 'bg-gradient-to-r from-ink to-ink-soft text-white shadow-soft hover:brightness-125 hover:shadow-lifted',
        /** Text-only, for tertiary actions. The wash arrives on hover. */
        ghost:
          'text-primary-strong hover:bg-gradient-to-r hover:from-primary/15 hover:to-primary/5',
        /** Destructive actions in the admin. Its own hue, same two-stop treatment. */
        danger:
          'bg-gradient-to-r from-error-strong to-error text-on-error shadow-soft ' +
          'hover:brightness-110 hover:shadow-lifted active:brightness-95',
        /** Neutral admin action — warms to the brand gradient under the cursor. */
        outline:
          'border border-outline-strong bg-surface text-heading ' +
          'hover:border-primary/40 hover:text-primary-strong ' +
          'hover:bg-gradient-to-r hover:from-primary/12 hover:to-primary/5',
      },
      size: {
        sm: 'h-9 px-4 text-sm',
        md: 'h-11 px-6 text-[0.95rem]',
        lg: 'h-14 px-8 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ComponentPropsWithoutRef<'button'>, VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, type = 'button', ...props },
  ref,
) {
  return (
    // `type="button"` by default: an unspecified button inside a form submits it,
    // which is a recurring source of accidental submissions.
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
});

export interface ButtonLinkProps
  extends
    Omit<ComponentPropsWithoutRef<typeof Link>, 'className'>,
    VariantProps<typeof buttonVariants> {
  className?: string;
}

/** A link styled as a button. Stays an anchor, so middle-click and copy-link work. */
export function ButtonLink({ className, variant, size, ...props }: ButtonLinkProps): ReactNode {
  return <Link className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}

/** An external link styled as a button, with the safe `rel` applied. */
export function ExternalButtonLink({
  className,
  variant,
  size,
  ...props
}: ComponentPropsWithoutRef<'a'> & VariantProps<typeof buttonVariants>): ReactNode {
  return (
    <a
      className={cn(buttonVariants({ variant, size }), className)}
      rel="noopener noreferrer"
      {...props}
    />
  );
}

// -----------------------------------------------------------------------------
// Card
// -----------------------------------------------------------------------------

const cardVariants = cva('rounded-card border transition-all', {
  variants: {
    variant: {
      /** White, 1px lavender border, diffused purple-tinted shadow. */
      default: 'bg-surface-raised border-outline shadow-card',
      /** Flat, for dense admin lists where shadows become noise. */
      flat: 'bg-surface-raised border-outline',
      /** Lifts on hover — for cards that are themselves links. */
      interactive:
        'bg-surface-raised border-outline shadow-card hover:shadow-lifted hover:-translate-y-0.5',
      /** On a wash background, where a white card needs no shadow to separate. */
      subtle: 'bg-surface-raised border-transparent',
      /**
       * Lit edges and a deep cast, rising under the cursor — see `.card-3d` in index.css.
       *
       * Distinct from `interactive`, which is a flat card that lifts. This one has
       * thickness whether or not you touch it, which is what the admin's panels want:
       * they sit on a lavender wash rather than on white, and a flat white rectangle on a
       * tinted ground reads as a hole rather than as a card.
       */
      raised: 'bg-surface-raised border-outline card-3d',
    },
    padding: {
      /** 32px internal padding, for a spacious feel. */
      default: 'p-8',
      compact: 'p-6',
      tight: 'p-4',
      none: 'p-0',
    },
  },
  defaultVariants: { variant: 'default', padding: 'default' },
});

export interface CardProps
  extends ComponentPropsWithoutRef<'div'>, VariantProps<typeof cardVariants> {}

export function Card({ className, variant, padding, children, ...props }: CardProps): ReactNode {
  return (
    <div className={cn(cardVariants({ variant, padding }), className)} {...props}>
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Chip / Badge
// -----------------------------------------------------------------------------

const chipVariants = cva(
  'inline-flex items-center gap-1.5 rounded-chip px-3 py-1 text-xs font-semibold',
  {
    variants: {
      tone: {
        default: 'bg-outline text-primary-strong',
        neutral: 'bg-surface-wash text-muted',
        success: 'bg-success-container text-success',
        warning: 'bg-warning-container text-warning',
        danger: 'bg-error-container text-error',
        ink: 'bg-ink/10 text-ink',
        outline: 'border border-outline-strong text-body',
      },
    },
    defaultVariants: { tone: 'default' },
  },
);

export interface ChipProps
  extends ComponentPropsWithoutRef<'span'>, VariantProps<typeof chipVariants> {}

export function Chip({ className, tone, children, ...props }: ChipProps): ReactNode {
  return (
    <span className={cn(chipVariants({ tone }), className)} {...props}>
      {children}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Icon container
// -----------------------------------------------------------------------------

/**
 * A soft lavender circle around an icon.
 *
 * The design specifies this so icons read as deliberate without competing with the
 * headline for attention.
 */
export function IconBubble({
  className,
  children,
  tone = 'default',
}: {
  className?: string;
  children: ReactNode;
  tone?: 'default' | 'ink' | 'white';
}): ReactNode {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex size-11 shrink-0 items-center justify-center rounded-full',
        tone === 'default' && 'bg-outline text-primary-strong',
        tone === 'ink' && 'bg-ink text-white',
        tone === 'white' && 'bg-white/10 text-white',
        className,
      )}
    >
      {children}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Form fields
// -----------------------------------------------------------------------------

/**
 * The look of a form control, without its padding.
 *
 * Split out so a composite control — the phone field, which is a button and an input
 * sharing one box — can wear the identical surface, radius, border and invalid state.
 * Re-typing those classes there is how two fields end up a few pixels apart.
 */
export const fieldSurface =
  'rounded-input bg-surface-wash text-heading ' +
  'border border-transparent transition-colors ' +
  'aria-[invalid=true]:border-error aria-[invalid=true]:bg-error-container/30';

/**
 * Padding, matched by every control so they line up on a row.
 *
 * `py-2.5` rather than `py-3`: the fields were 53px tall, which reads as heavy next to
 * 15px labels and made a four-field form look like a form twice its length. This lands
 * them at about 46px — still above the 44px touch-target floor, so nothing gets harder
 * to tap on a phone.
 */
export const fieldPadding = 'px-4 py-2.5';

/*
  `text-[15px] leading-normal`, not `text-body-md`.

  That token is 16px on a 1.7 line-height — correct for a paragraph, far too airy inside
  a control, where it added 11px of leading to every box and made a four-field form look
  inflated. 15px on normal leading is the same type a hair smaller in a box a hair
  shorter, and the fields still clear the 44px touch-target floor.
*/
const fieldBase =
  `w-full ${fieldSurface} ${fieldPadding} text-[15px] leading-normal ` +
  'placeholder:text-muted/60 ' +
  'focus:border-primary focus:bg-surface focus:outline-none ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

export type InputProps = ComponentPropsWithoutRef<'input'>;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, ...props },
  ref,
) {
  return <input ref={ref} className={cn(fieldBase, className)} {...props} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, ComponentPropsWithoutRef<'textarea'>>(
  function Textarea({ className, rows = 5, ...props }, ref) {
    return (
      <textarea ref={ref} rows={rows} className={cn(fieldBase, 'resize-y', className)} {...props} />
    );
  },
);

export interface SelectOption {
  value: string;
  label: string;
  /** An optional second line, for an option whose label needs qualifying. */
  hint?: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  /** Bound to a `<Field>`'s `htmlFor`, so clicking the label focuses the trigger. */
  id?: string;
  disabled?: boolean;
  className?: string;
  /** Shown when `value` matches no option — an unset filter, typically. */
  placeholder?: string;
  /** `sm` is the inline control used inside a table row. */
  size?: 'sm' | 'md';
  'aria-label'?: string;
}

/**
 * A select.
 *
 * ## Why this is not a `<select>`
 *
 * It was, and the popup could not be styled. A native option list is painted by the
 * operating system: it ignores the font, the radius, the purple, and — the reason this
 * finally had to change — the console's dark theme, so choosing a status on a dark screen
 * opened a stark white OS menu. `appearance: none` restyles the *closed* control only;
 * there is no CSS that reaches inside the open list. Rebuilding it as a listbox is the
 * only way to make the open state look like the rest of the product.
 *
 * ## What that costs, and how it is paid
 *
 * A native select is accessible for free, so everything it gave away has to be rebuilt:
 *
 *  - The ARIA 1.2 combobox pattern. Focus stays on the trigger and the active option is
 *    named by `aria-activedescendant`, rather than moving DOM focus into the list. That
 *    is markedly simpler to get right — there is exactly one focused element at all times
 *    — and it is what screen readers expect from a collapsed-select widget.
 *  - Full keyboard control: arrows, Home/End, Enter and Space to commit, Escape to
 *    cancel, Tab to leave. Typing a letter jumps to the next option starting with it,
 *    which is the one native affordance people miss most.
 *  - `disabled` that genuinely cannot be opened — by pointer *or* keyboard. Two of the
 *    three call sites gate a permission with it (`isNew || !canPublish` on the post
 *    editor), so a trigger that still opened when disabled would let an author set a
 *    status their role cannot publish.
 *
 * ## Why the list is portalled
 *
 * Both list screens wrap their card in `overflow-hidden` to clip the rows to its rounded
 * corners, and the enquiries status control sits inside an expanded row *inside* that
 * card. Positioned inline, the popup is cut off at the card's edge. Rendering it into
 * `document.body` at fixed coordinates escapes every ancestor's clipping and stacking
 * context at once.
 *
 * That works here only because the theme is an attribute on `<html>` rather than on a
 * wrapper — the portal is outside the console's markup but still inside the document, so
 * it inherits the dark palette. See the note in `pages/admin/theme.ts`; this is the
 * second thing that decision buys.
 */
export function Select({
  options,
  value,
  onChange,
  id,
  disabled = false,
  className,
  placeholder = 'Select…',
  size = 'md',
  'aria-label': ariaLabel,
}: SelectProps): ReactNode {
  const generatedId = useId();
  const triggerId = id ?? `${generatedId}-trigger`;
  const listId = `${generatedId}-list`;
  const optionId = (index: number): string => `${generatedId}-option-${index}`;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  /** Accumulated type-ahead keystrokes, and when the last one landed. */
  const typeAhead = useRef<{ text: string; at: number }>({ text: '', at: 0 });

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );

  const selectedIndex = options.findIndex((option) => option.value === value);
  const [activeIndex, setActiveIndex] = useState(Math.max(0, selectedIndex));

  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  /**
   * Measure the trigger and decide which way the list opens.
   *
   * `useLayoutEffect`, not `useEffect`: the popup is positioned from a measurement, and
   * in a passive effect the browser paints one frame at the default coordinates first —
   * a visible jump from the top-left of the window into place.
   */
  useLayoutEffect(() => {
    if (!open) return;

    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    // Roughly the popup's height: 40px a row, capped by its own max-height.
    const estimated = Math.min(options.length * 40 + 8, 288);
    const below = window.innerHeight - rect.bottom;
    // Flip above only when there is genuinely more room there — otherwise a control near
    // the bottom of a short window flips for a few pixels and reads as a glitch.
    const flip = below < estimated && rect.top > below;

    setPosition({
      top: flip ? rect.top - estimated - 6 : rect.bottom + 6,
      left: rect.left,
      width: rect.width,
    });
  }, [open, options.length]);

  /**
   * Dismiss on anything that invalidates the measured position or the user's intent.
   *
   * Scroll is listened for in the capture phase so a scrolling *ancestor* is caught, not
   * just the window — the popup is fixed-positioned and would otherwise hang in place
   * while the control it belongs to slides away underneath it.
   */
  useEffect(() => {
    if (!open) return;

    const close = (): void => setOpen(false);

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      close();
    };

    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  // Keep the active option in view when the arrow keys walk past the visible window.
  useEffect(() => {
    if (!open) return;

    const active = listRef.current?.querySelector('[data-active="true"]');
    /*
      Feature-detected rather than called straight. `scrollIntoView` is on the DOM type
      but jsdom does not implement it, so an unguarded call throws inside a passive effect
      — which React surfaces as an unhandled error and takes the whole tree down. Losing
      the scroll adjustment in a test environment costs nothing; throwing costs the test.
    */
    if (active instanceof HTMLElement && typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ block: 'nearest' });
    }
  }, [open, activeIndex]);

  const openList = (index: number): void => {
    if (disabled) return;
    setActiveIndex(Math.max(0, index));
    setOpen(true);
  };

  const commit = (index: number): void => {
    const option = options[index];
    if (!option || option.disabled) return;
    onChange(option.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  /** Step to the next selectable option, skipping disabled ones. */
  const step = (from: number, delta: number): number => {
    for (let i = 1; i <= options.length; i += 1) {
      const next = from + delta * i;
      if (next < 0 || next >= options.length) break;
      if (!options[next]?.disabled) return next;
    }
    return from;
  };

  const edge = (direction: 'first' | 'last'): number => {
    const indexes = options.map((_, index) => index);
    const ordered = direction === 'first' ? indexes : indexes.reverse();
    return ordered.find((index) => !options[index]?.disabled) ?? activeIndex;
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>): void => {
    // The whole widget is inert when disabled — pointer and keyboard alike.
    if (disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) openList(selectedIndex);
        else setActiveIndex(step(activeIndex, 1));
        return;
      case 'ArrowUp':
        event.preventDefault();
        if (!open) openList(selectedIndex);
        else setActiveIndex(step(activeIndex, -1));
        return;
      case 'Home':
        if (!open) return;
        event.preventDefault();
        setActiveIndex(edge('first'));
        return;
      case 'End':
        if (!open) return;
        event.preventDefault();
        setActiveIndex(edge('last'));
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!open) openList(selectedIndex);
        else commit(activeIndex);
        return;
      case 'Escape':
        if (!open) return;
        event.preventDefault();
        setOpen(false);
        return;
      case 'Tab':
        // Not prevented: Tab should move on. The list just must not be left behind.
        setOpen(false);
        return;
      default:
        break;
    }

    /*
      Type-ahead. Keystrokes within a second of each other accumulate, so "sc" reaches
      Scheduled rather than stopping at anything starting with S — which is how a native
      select behaves and the reason people type at all instead of arrowing.
    */
    if (event.key.length !== 1 || event.altKey || event.ctrlKey || event.metaKey) return;

    const now = event.timeStamp;
    const text = (now - typeAhead.current.at < 1000 ? typeAhead.current.text : '') + event.key;
    typeAhead.current = { text, at: now };

    const match = options.findIndex(
      (option) => !option.disabled && option.label.toLowerCase().startsWith(text.toLowerCase()),
    );
    if (match < 0) return;

    event.preventDefault();
    if (open) setActiveIndex(match);
    else commit(match);
  };

  const list =
    open && position ? (
      <ul
        ref={listRef}
        id={listId}
        role="listbox"
        aria-labelledby={triggerId}
        aria-activedescendant={optionId(activeIndex)}
        style={{ top: position.top, left: position.left, minWidth: position.width }}
        className={cn(
          'fixed z-[60] max-h-72 overflow-y-auto rounded-input border border-outline bg-surface p-1 shadow-lifted',
          // The popup owns its own scrollbar; `overscroll-contain` stops a flick at its
          // end from scrolling the page behind it.
          'overscroll-contain',
        )}
      >
        {options.map((option, index) => {
          const isSelected = option.value === value;
          const isActive = index === activeIndex;

          return (
            <li
              key={option.value}
              id={optionId(index)}
              role="option"
              aria-selected={isSelected}
              aria-disabled={option.disabled || undefined}
              data-active={isActive}
              /*
                `onMouseDown` with `preventDefault`, not `onClick`: a click fires after
                the mousedown has already moved focus off the trigger, which closes the
                list via the outside-pointer handler before the selection lands.
              */
              onMouseDown={(event) => {
                event.preventDefault();
                commit(index);
              }}
              onMouseEnter={() => !option.disabled && setActiveIndex(index)}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded-[0.5rem] px-3 py-2 text-sm transition-colors',
                option.disabled && 'cursor-not-allowed opacity-40',
                isSelected ? 'font-semibold text-primary-strong' : 'text-body',
                // The hover/active tint is the same brand wash the sidebar entries use.
                isActive && !option.disabled && 'bg-primary/10 text-primary-strong',
              )}
            >
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate">{option.label}</span>
                {option.hint ? (
                  <span className="truncate text-xs font-normal text-muted">{option.hint}</span>
                ) : null}
              </span>

              {/* A tick on the current value — `aria-selected` already says so, hence
                  `aria-hidden` here. */}
              {isSelected ? (
                <Check aria-hidden="true" className="size-4 shrink-0 text-primary" />
              ) : null}
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? optionId(activeIndex) : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList(selectedIndex))}
        onKeyDown={onKeyDown}
        className={cn(
          fieldSurface,
          'flex w-full items-center justify-between gap-2 text-left',
          size === 'sm' ? 'px-3 py-1.5 text-[13px]' : fieldPadding + ' text-[15px] leading-normal',
          'cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
          open && 'border-primary/50',
          className,
        )}
      >
        <span className={cn('truncate', !selected && 'text-muted')}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'size-4 shrink-0 text-muted transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>

      {list ? createPortal(list, document.body) : null}
    </>
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  error?: string | undefined;
  hint?: string | undefined;
  required?: boolean;
  className?: string;
  /**
   * `caps` renders the label as a small uppercase eyebrow.
   *
   * The reference design uses that treatment for the homepage contact block and the
   * sentence-case one everywhere else. It is a variant rather than a `className`
   * override so the two forms cannot drift apart.
   */
  labelVariant?: 'default' | 'caps';
  children: ReactNode;
}

/**
 * Label, control, hint and error as one unit.
 *
 * The pieces are wired together properly: `htmlFor` binds the label so clicking it
 * focuses the control, and the error is given an id that the control references via
 * `aria-describedby`, so a screen reader announces the failure rather than leaving the
 * user with a red border they cannot perceive.
 */
export function Field({
  label,
  htmlFor,
  error,
  hint,
  required,
  className,
  labelVariant = 'default',
  children,
}: FieldProps): ReactNode {
  const errorId = `${htmlFor}-error`;
  const hintId = `${htmlFor}-hint`;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={htmlFor}
        className={cn(
          labelVariant === 'caps'
            ? 'text-eyebrow font-bold uppercase tracking-[0.1em] text-body'
            : // 13px medium, not 14px semibold: at the old weight the labels competed
              // with the section heading above them for the same emphasis.
              'text-[13px] font-medium text-heading',
        )}
      >
        {label}
        {required ? (
          <span className="ml-1 text-error" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      {hint ? (
        <p id={hintId} className="text-[13px] text-muted">
          {hint}
        </p>
      ) : null}

      {children}

      {error ? (
        // `role="alert"` so the message is announced when it appears, not only when
        // the user happens to navigate onto it.
        <p id={errorId} role="alert" className="text-[13px] font-medium text-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Switch
// -----------------------------------------------------------------------------

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /**
   * The accessible name. Required rather than optional: a bare toggle announces as
   * "switch, on" with nothing to say what it switches, which is useless to anyone
   * not looking at the row it sits in.
   */
  label: string;
  /** Rendered beside the track. Omit where the surrounding row already names it. */
  showLabel?: boolean;
  disabled?: boolean;
  /** Dims the control and blocks input while a save is in flight. */
  busy?: boolean;
  className?: string;
}

/**
 * An on/off switch.
 *
 * A `role="switch"` button rather than a styled checkbox. Both are valid, but a
 * checkbox announces as "checked / not checked" — the language of a form you submit —
 * where this control takes effect the moment it is pressed. "On / off" is what it
 * actually does, and `switch` is the role that says so.
 *
 * The text beside the track is part of the control, not a label next to it, so the
 * word "On" is inside the button: a user who taps the word rather than the track has
 * done the same thing either way, which on a phone is most of them.
 */
export function Switch({
  checked,
  onChange,
  label,
  showLabel = true,
  disabled = false,
  busy = false,
  className,
}: SwitchProps): ReactNode {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      onClick={() => onChange(!checked)}
      className={cn(
        'inline-flex items-center gap-2.5 rounded-full transition-opacity',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        (disabled || busy) && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'relative h-6 w-11 shrink-0 rounded-full transition-colors',
          checked ? 'bg-primary' : 'bg-outline-strong',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 size-5 rounded-full bg-white shadow-sm transition-all',
            checked ? 'left-[1.375rem]' : 'left-0.5',
          )}
        />
      </span>
      {showLabel ? (
        <span
          className={cn(
            'w-7 text-left text-sm font-semibold',
            checked ? 'text-primary-strong' : 'text-muted',
          )}
        >
          {checked ? 'On' : 'Off'}
        </span>
      ) : null}
    </button>
  );
}

// -----------------------------------------------------------------------------
// Feedback
// -----------------------------------------------------------------------------

/** Loading placeholder. Animation is suppressed under `prefers-reduced-motion`. */
export function Skeleton({ className }: { className?: string }): ReactNode {
  return (
    <div aria-hidden="true" className={cn('animate-pulse rounded-md bg-outline', className)} />
  );
}

/**
 * A callout.
 *
 * ## Why `info` is a purple tint and not the page wash
 *
 * It used to be `bg-surface-wash`, and the admin shell's own ground is `bg-surface-wash`
 * — so every info alert in the console was a rectangle the same colour as the page it sat
 * on, separated from it by one pixel of `border-outline`. It read as a stray paragraph
 * rather than as a callout, which is the same failure `ListToolbar` was written to fix for
 * the filter strips. A faint primary tint has a real edge against both grounds it lands
 * on — the wash *and* a white card — in both themes, which is what a callout has to do to
 * be one.
 *
 * That matters more than it sounds: the fix for a screen carrying too much prose is to
 * turn the prose into callouts, and doing that with an invisible component makes the
 * screen worse while appearing to make it better.
 *
 * ## The icon
 *
 * Defaulted per tone rather than passed at each of the twenty call sites, because the
 * mapping from severity to glyph is a design decision and not a per-screen one. Always
 * `aria-hidden`: the tone is already conveyed by `role` and by the title's wording, and a
 * screen reader announcing "image" before every callout is noise. Pass `icon={false}` for
 * the rare callout that is a bare sentence.
 */
export function Alert({
  tone = 'danger',
  title,
  children,
  className,
  icon,
  compact = false,
}: {
  tone?: 'danger' | 'warning' | 'success' | 'info';
  title?: string;
  children: ReactNode;
  className?: string;
  /** Overrides the per-tone default. `false` renders no icon at all. */
  icon?: ReactNode | false;
  /**
   * One line rather than two: the title runs inline with the body.
   *
   * For the short "why is this the way it is" notes that belong beside a control rather
   * than above a section — a full-height callout for eight words is heavier than the
   * eight words deserve.
   */
  compact?: boolean;
}): ReactNode {
  const tones = {
    danger: 'bg-error-container/50 border-error/30 text-error',
    warning: 'bg-warning-container border-warning/30 text-warning',
    success: 'bg-success-container border-success/30 text-success',
    info: 'bg-primary/[0.06] border-primary/25 text-body',
  } as const;

  const icons: Record<'danger' | 'warning' | 'success' | 'info', LucideIcon> = {
    danger: CircleAlert,
    warning: TriangleAlert,
    success: CircleCheck,
    info: Info,
  };

  const ToneIcon = icons[tone];
  const glyph =
    icon === false ? null : (icon ?? <ToneIcon aria-hidden="true" className="size-4 shrink-0" />);

  return (
    <div
      // Assertive for errors so they interrupt; polite otherwise so they do not.
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex gap-2.5 rounded-input border text-sm',
        compact ? 'items-center px-3 py-2' : 'px-4 py-3',
        tones[tone],
        className,
      )}
    >
      {/* `mt-0.5` in the tall variant optically centres a 16px glyph on the first line of
          14px text; in the compact variant the row is centred so it needs no nudge. */}
      {glyph ? <span className={cn('shrink-0', !compact && 'mt-0.5')}>{glyph}</span> : null}

      <div className="min-w-0 flex-1">
        {title ? (
          <p className={cn('font-semibold', compact && 'inline')}>
            {title}
            {compact ? ' ' : null}
          </p>
        ) : null}
        <div className={cn(compact ? 'inline' : title && 'mt-1')}>{children}</div>
      </div>
    </div>
  );
}

/**
 * A row of mutually exclusive options — the control that picks which menu, which preview
 * mode, which status filter.
 *
 * ## Why this exists
 *
 * Five hand-rolled copies of it did: the enquiry list's status filters, the post preview's
 * viewport switcher, and three more besides. They had drifted into different radii,
 * different active colours and different accessibility stories, which is the usual fate of
 * a pattern that never got a name.
 *
 * ## Why `aria-pressed` buttons rather than `role="tablist"`
 *
 * A tablist promises arrow-key navigation between tabs and a `tabpanel` that is labelled
 * by the selected one. These controls filter or switch content in place, and claiming the
 * pattern without implementing it describes the widget to a screen reader as something it
 * is not. A group of toggle buttons is what this actually is, so that is what it says —
 * the same reasoning the enquiry filters already carried in a comment.
 */
export interface ToggleGroupOption {
  value: string;
  label: string;
  /** A figure shown after the label — an unanswered count, a number of entries. */
  count?: number;
  /** Native tooltip, for a label that needs qualifying without spending a line on it. */
  hint?: string;
}

export function ToggleGroup({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  size = 'md',
  className,
}: {
  options: ToggleGroupOption[];
  value: string;
  onChange: (value: string) => void;
  /** Names the group, so its buttons are not announced as five unrelated toggles. */
  'aria-label': string;
  size?: 'sm' | 'md';
  className?: string;
}): ReactNode {
  return (
    <div role="group" aria-label={ariaLabel} className={cn('flex flex-wrap gap-2', className)}>
      {options.map((option) => {
        const isActive = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            title={option.hint}
            aria-pressed={isActive}
            className={cn(
              'inline-flex items-center gap-2 rounded-full border font-medium transition-colors',
              size === 'sm' ? 'px-3 py-1 text-xs' : 'px-3.5 py-1.5 text-sm',
              isActive
                ? 'border-primary bg-primary text-on-primary'
                : 'border-outline-strong bg-surface text-body hover:bg-surface-wash',
            )}
          >
            {option.label}
            {option.count !== undefined && option.count > 0 ? (
              <span
                className={cn(
                  'inline-flex min-w-4 items-center justify-center rounded-full px-1 text-[11px] font-bold leading-none',
                  isActive ? 'bg-white/25 text-white' : 'bg-primary text-on-primary',
                )}
              >
                {/* Capped, so a large figure cannot stretch the control. */}
                {option.count > 99 ? '99+' : option.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A show/hide for detail that is worth having and not worth reading twice.
 *
 * The alternative to a disclosure, for the paragraph explaining what turning a page off
 * does, is either a permanent two-paragraph callout — which everybody who has read it once
 * then scrolls past forever — or deleting it, which strands the person meeting the control
 * for the first time. This is the shape that serves both.
 *
 * A `<button aria-expanded>` plus a region, not `<details>`/`<summary>`: the native pair
 * cannot be styled consistently across browsers (Safari's marker in particular) and its
 * open state is DOM state rather than React state, which puts it outside the component's
 * control. Content is unmounted when closed rather than hidden, so nothing inside it is
 * reachable by Tab or announced while it is shut.
 */
export function Disclosure({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const id = useId();

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={id}
        className="inline-flex items-center gap-1 text-xs font-semibold text-primary-strong hover:underline"
      >
        <ChevronDown
          aria-hidden="true"
          className={cn('size-3.5 transition-transform', open && 'rotate-180')}
        />
        {label}
      </button>

      {open ? (
        <div id={id} className="mt-2 text-sm text-body">
          {children}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Shown where a list has no rows.
 *
 * An explicit component rather than an ad-hoc `<p>No results</p>`, because an empty
 * state is a real part of the interface: it should say why it is empty and what to do
 * next, and having a component makes that the path of least resistance.
 */
export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-card border border-dashed border-outline-strong px-6 py-16 text-center',
        className,
      )}
    >
      {icon ? <IconBubble>{icon}</IconBubble> : null}
      <p className="text-h3 font-semibold text-heading">{title}</p>
      {description ? <p className="max-w-md text-body">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

/** Bulleted list with primary-coloured check marks, per the design. */
export function CheckList({
  items,
  className,
  tone = 'default',
}: {
  items: string[];
  className?: string;
  tone?: 'default' | 'inverse';
}): ReactNode {
  return (
    <ul className={cn('flex flex-col gap-4', className)}>
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <svg
            aria-hidden="true"
            viewBox="0 0 20 20"
            className={cn(
              'mt-1 size-5 shrink-0',
              tone === 'inverse' ? 'text-secondary-container' : 'text-primary',
            )}
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 0 1 1.4-1.4l3.8 3.8 6.8-6.8a1 1 0 0 1 1.4 0Z"
              clipRule="evenodd"
            />
          </svg>
          <span className={tone === 'inverse' ? 'text-white/80' : 'text-body'}>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export { PhoneInput } from './PhoneInput';
